import React from "react";
import { Tooltip } from "antd";
import { TaskPriceBreakdown } from "./taskPrice";

/**
 * Read-only display of a task's price split.
 *
 * The user types one number — the GST-inclusive contract rate. This panel shows what
 * the system derives from it, ending in the Basic Rate: the rate net of GST (a
 * pass-through) and BOCW labour cess (withheld by the client), which is what every
 * costing and P&L report multiplies quantity by.
 */

const INK = "#022c57";
const MUTED = "#8094ae";
const HAIRLINE = "#e4e9f0";

const fmt = (value: number): string =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Cell: React.FC<{
  label: string;
  value: number;
  emphasis?: boolean;
  tooltip?: React.ReactNode;
}> = ({ label, value, emphasis, tooltip }) => {
  const body = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
        cursor: tooltip ? "help" : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          color: MUTED,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasis ? 16 : 14,
          fontWeight: emphasis ? 700 : 500,
          color: emphasis ? INK : "#364a63",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          // Dotted underline is the affordance that there's more on hover.
          borderBottom: tooltip ? `1px dotted ${MUTED}` : undefined,
        }}
      >
        {fmt(value)}
      </span>
    </div>
  );
  return tooltip ? <Tooltip title={tooltip}>{body}</Tooltip> : body;
};

/**
 * Hover breakdown for the quoted rate: how ₹2,195.00 resolves to the Basic Rate.
 * Reused by the panel and by any other surface that shows the incl-GST rate.
 */
export const rateInclGstTooltip = (
  breakdown: TaskPriceBreakdown,
  gstPercent = 18,
  cessPercent = 1,
): React.ReactNode => {
  const row = (label: string, value: string, strong = false) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        fontWeight: strong ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
  return (
    <div style={{ fontSize: 12, lineHeight: 1.8, minWidth: 232 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Rate breakdown</div>
      {row("Rate excl. GST", fmt(breakdown.baseRate))}
      {row(`GST ${gstPercent}%`, `+ ${fmt(breakdown.gstAmount)}`)}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.25)",
          margin: "4px 0",
        }}
      />
      {row("Rate incl. GST", fmt(breakdown.rate))}
      {row(`Labour Cess ${cessPercent}%`, `− ${fmt(breakdown.cessAmount)}`)}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.25)",
          margin: "4px 0",
        }}
      />
      {row("Basic Rate", fmt(breakdown.uploadRate), true)}
      <div style={{ marginTop: 6, opacity: 0.85, fontSize: 11 }}>
        GST is a pass-through and labour cess is withheld by the client, so the Basic
        Rate is what the project earns — every costing and P&amp;L figure uses it.
      </div>
    </div>
  );
};

const TaskPriceBreakdownPanel: React.FC<{
  breakdown: TaskPriceBreakdown;
  gstPercent?: number;
  cessPercent?: number;
}> = ({ breakdown, gstPercent = 18, cessPercent = 1 }) => {
  const empty = !breakdown || breakdown.rate <= 0;

  return (
    <div
      style={{
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 8,
        padding: "12px 16px",
        background: "#fbfcfe",
        marginBottom: 16,
      }}
    >
      {empty ? (
        <span style={{ fontSize: 13, color: MUTED }}>
          Enter a rate to see the base price, GST and labour cess breakdown.
        </span>
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: 24,
          }}
        >
          <Cell label="Rate excl. GST" value={breakdown.baseRate} />
          <span style={{ color: MUTED, fontSize: 14, paddingBottom: 2 }}>+</span>
          <Cell label={`GST ${gstPercent}%`} value={breakdown.gstAmount} />
          <span style={{ color: MUTED, fontSize: 14, paddingBottom: 2 }}>=</span>
          <Cell
            label="Rate incl. GST"
            value={breakdown.rate}
            tooltip={rateInclGstTooltip(breakdown, gstPercent, cessPercent)}
          />
          <span
            style={{
              alignSelf: "stretch",
              borderLeft: `1px solid ${HAIRLINE}`,
              margin: "0 4px",
            }}
          />
          <Cell label={`Less Labour Cess ${cessPercent}%`} value={breakdown.cessAmount} />
          <Cell label="Basic Rate (working)" value={breakdown.uploadRate} emphasis />
        </div>
      )}
    </div>
  );
};

export default TaskPriceBreakdownPanel;
